import './BubbleSelect.scss';

import React from 'react';
import { hasOwnProperty } from '@go-shared/lib/utils';
import { groupBy } from '@go-shared/lib/functional';
import { DefaultedText } from '../DefaultedText';

interface BubbleSelectPropsBase<T> {
  value?: T;
  options?: T[];
  block?: boolean;
  selectRef?: React.RefObject<HTMLSelectElement>;
  selectClassName?: string;
  selectStyle?: React.CSSProperties;
  appearance?: 'rounded' | 'bare';
  readOnly?: boolean;
  variant?: 'velvet' | 'navy' | 'titanium';
  groupKeyExtractor?: (value: T) => string;
  groupLabelExtractor?: (key: string) => string;
  keyExtractor?: (value: T) => string;
  labelExtractor?: (value: T) => string;
  internalValueExtractor?: (value: T) => string | number;
  undefinedLabel?: string;
}

interface BubbleSelectPropsWithUndefined<T> {
  allowUndefined: true;
  onValue?: (value?: T) => void;
}

interface BubbleSelectPropsNoUndefined<T> {
  allowUndefined: false;
  onValue?: (value: T) => void;
}

const __UNDEFINED__ = '__UNDEFINED__';

export type BubbleSelectProps<T> = Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'value'> &
  BubbleSelectPropsBase<T> &
  (BubbleSelectPropsWithUndefined<T> | BubbleSelectPropsNoUndefined<T>);

export default function BubbleSelect<T extends string | number | object>(
  props: BubbleSelectProps<T>
) {
  const {
    // from component
    value,
    options: propOptions,
    block,
    selectRef,
    selectClassName: propSelectClassName,
    selectStyle: propSelectStyle,
    allowUndefined,
    undefinedLabel,
    variant,
    appearance,
    groupKeyExtractor,
    keyExtractor,
    labelExtractor,
    groupLabelExtractor,
    internalValueExtractor,
    onValue,
    readOnly,
    // from element
    className: propClassName,
    style: propStyle,
    onChange,
    ...rest
  } = props;

  type BubbleGroupedOption = {
    key: string;
    options: T[];
  };

  const options = propOptions || [];
  let groupedOptions: BubbleGroupedOption[] | undefined;

  if (groupKeyExtractor) {
    const groupedOptionsMap = groupBy(options, groupKeyExtractor);
    groupedOptions = Object.keys(groupedOptionsMap).map(k => ({
      key: k,
      options: groupedOptionsMap[k],
    }));
  }

  function resolveInternalValue(val: T) {
    if (internalValueExtractor) return internalValueExtractor(val);
    else if (typeof val === 'object') return JSON.stringify(val);
    else return `${val}`;
  }

  function resolveLabel(val: T) {
    if (labelExtractor) return labelExtractor(val);
    else return resolveInternalValue(val);
  }

  function resolveGroupLabel(key: string) {
    if (groupLabelExtractor) return groupLabelExtractor(key);
    else return key;
  }

  function resolveKey(val: T) {
    if (keyExtractor) {
      return keyExtractor(val);
    } else if (hasOwnProperty(val, 'key') && ['string', 'number'].includes(typeof val.key)) {
      return `${val.key}`;
    } else {
      return resolveInternalValue(val);
    }
  }

  function onChangeHandler(event: React.ChangeEvent<HTMLSelectElement>) {
    event.persist();
    if (onChange) onChange(event);

    const valToSend = options[event.target.selectedIndex - (props.allowUndefined ? 1 : 0)];

    if (props.allowUndefined && props.onValue) {
      props.onValue(valToSend === __UNDEFINED__ ? undefined : valToSend);
    } else if (!props.allowUndefined && props.onValue) {
      props.onValue(valToSend);
    }
  }

  const classes = ['bubble-select-container'];
  if (block) classes.push('full');
  if (appearance) classes.push(appearance);
  if (propClassName) classes.push(propClassName);

  let style: React.CSSProperties = {};
  if (propStyle) style = { ...style, ...propStyle };

  const selectClasses = ['bubble-select'];
  if (variant) selectClasses.push(variant);
  if (propSelectClassName) selectClasses.push(propSelectClassName);

  let selectStyle: React.CSSProperties = {};
  if (propSelectStyle) selectStyle = { ...selectStyle, ...propSelectStyle };

  const arrowClasses = ['bubble-select-arrow'];
  if (variant) arrowClasses.push(variant);

  const createOption = (opt: T) => (
    <option key={resolveKey(opt)} value={resolveInternalValue(opt)}>
      {resolveLabel(opt)}
    </option>
  );

  const internalValue = value === undefined ? __UNDEFINED__ : resolveInternalValue(value);
  const currentOption = options.find(o => resolveInternalValue(o) === internalValue);

  return (
    <div className={classes.join(' ')} style={style}>
      {readOnly ? (
        <DefaultedText
          val={
            currentOption
              ? `${resolveLabel(currentOption)}`
              : !allowUndefined
              ? `${resolveLabel(options[0])}`
              : undefined
          }
          def={undefinedLabel || 'None'}
        />
      ) : (
        <>
          <select
            {...rest}
            ref={selectRef}
            value={internalValue}
            className={selectClasses.join(' ')}
            style={selectStyle}
            onChange={onChangeHandler}
          >
            {props.allowUndefined && (
              <option key={__UNDEFINED__} value={__UNDEFINED__}>
                {undefinedLabel || 'None'}
              </option>
            )}
            {groupedOptions
              ? groupedOptions.map(grp => (
                  <optgroup key={grp.key} label={resolveGroupLabel(grp.key)}>
                    {grp.options.map(createOption)}
                  </optgroup>
                ))
              : options.map(createOption)}
          </select>
          <div className={arrowClasses.join(' ')}>
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24">
              <path d="M0 7.33l2.829-2.83 9.175 9.339 9.167-9.339 2.829 2.83-11.996 12.17z" />
            </svg>
          </div>
        </>
      )}
    </div>
  );
}
